import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import NotificationList from 'components/notifications/NotificationList';
import { getNotificationsFromApi, getSeedNotifications, markAllNotificationsReadInApi, markNotificationReadInApi } from 'data/notifications';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const anchorRef = useRef(null);
  const [notifications, setNotifications] = useState(() => getSeedNotifications());
  const [open, setOpen] = useState(false);

  const unreadCount = useMemo(() => notifications.filter((notification) => notification.isUnread).length, [notifications]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    let isMounted = true;

    const loadNotifications = async () => {
      try {
        const items = await getNotificationsFromApi();
        if (isMounted) {
          setNotifications(items);
        }
      } catch {
        // Keep seed notifications as a safe fallback when API cannot be reached.
      }
    };

    loadNotifications();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const markAllAsRead = async () => {
    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) => ({
        ...notification,
        isUnread: false
      }))
    );

    try {
      await markAllNotificationsReadInApi();
    } catch {
      // Keep optimistic UI state and retry after next refresh.
    }
  };

  const handleNotificationClick = async (notification) => {
    setNotifications((currentNotifications) =>
      currentNotifications.map((currentNotification) => {
        if (currentNotification.id !== notification.id) {
          return currentNotification;
        }

        return {
          ...currentNotification,
          isUnread: false
        };
      })
    );

    try {
      await markNotificationReadInApi(notification.id);
    } catch {
      // Keep optimistic UI state and retry after next refresh.
    }

    setOpen(false);

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate(unreadCount > 0 ? '/notifications?filter=unread' : '/notifications');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          marginRight: '10px',
          marginLeft: '0px',
        })}
        aria-label={t('header.openNotifications')}
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={t('header.notificationsTitle')}
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title={t('header.markAllReadTooltip')}>
                          <IconButton color="success" size="small" onClick={markAllAsRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <NotificationList notifications={notifications} onItemClick={handleNotificationClick} />
                  <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }} onClick={handleViewAll}>
                    <ListItemText
                      primary={
                        <Typography variant="h6" color="primary">
                          {t('header.viewAllNotifications')}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
